/**
 * The review dialog's presentation logic, as pure functions.
 *
 * Lifted out of the component for one reason: these decide what a clinician is offered, and two
 * of them are safety rules rather than layout.
 *
 *  - `buildDecisions` collapses coupled suggestions into ONE decision. The server enforces the
 *    same coupling at the write (§11.13); this makes the buttons describe what the write will
 *    actually do. If the two ever disagree the server is the honest one — this exists so they
 *    do not.
 *  - `bulkAcceptable` decides what `Accept all` will take. Nothing on the server distinguishes a
 *    bulk click from a single one, so this is the ONLY thing keeping §7 rule 6 — removals are
 *    never applied without a human — from being undone by one button.
 *
 * Pure and dependency-free so `scripts/icd-coding-set-view-check.js` can exercise them directly.
 * A rule that only runs inside a component is a rule nothing checks.
 */

/** A suggestion card, after the component has collapsed cross-episode duplicates onto one. */
export interface SuggestionCard {
    id: string;
    ids: string[];
    action: string;
    code: string;
    description?: string;
    proposedRole?: string | null;
    replacesCodeId?: string | null;
    decisionGroupId?: string | null;
    decisionKind?: string | null;
    supersededByCode?: string | null;
    pairingWarning?: string | null;
    [key: string]: any;
}

export interface Decision {
    key: string;
    kind: 'single' | 'together' | 'exclusive';
    members: SuggestionCard[];
    lead: SuggestionCard;
    ids: string[];
    message: string | null;
}

export interface CodingSetRow {
    code: string;
    description?: string;
    role: string | null;
    newRole: string | null;
    state: 'keep' | 'adding' | 'removing' | 'choice' | 'role-change';
    decisionKey: string | null;
    current: any | null;
    suggestion: SuggestionCard | null;
}

/**
 * Ungrouped cards get a key of their own so every decision has one, coupled or not.
 *
 * ⚠ A `severable` group is deliberately NOT collapsed. It is the two halves of a split
 * `replace` — an addition and a removal that came from one proposal but are decided
 * independently (`severReplacements` on the server). Keying them together would rebuild the
 * single all-or-nothing card that splitting them existed to get rid of.
 */
const keyFor = (card: SuggestionCard): string =>
    (card.decisionKind === 'severable' ? null : card.decisionGroupId) || `single:${card.id}`;

/**
 * Collapses coupled suggestions into single decisions, preserving the server's ordering.
 *
 * A group whose siblings have already been resolved elsewhere degrades to a plain single card
 * rather than rendering a one-member "pair", which would be a lie about a choice.
 */
export function buildDecisions(cards: SuggestionCard[]): Decision[] {
    const byGroup = new Map<string, SuggestionCard[]>();
    const order: string[] = [];

    for (const card of cards) {
        const key = keyFor(card);
        if (!byGroup.has(key)) { byGroup.set(key, []); order.push(key); }
        (byGroup.get(key) as SuggestionCard[]).push(card);
    }

    return order.map((key) => {
        const members = byGroup.get(key) as SuggestionCard[];
        const kind: Decision['kind'] = members.length > 1
            ? ((members[0].decisionKind as Decision['kind']) || 'single')
            : 'single';

        // For a choice, the code that supersedes nothing is the specific one being recommended;
        // the one carrying `supersededByCode` is the catch-all it would displace.
        if (kind === 'exclusive') {
            members.sort((a, b) => (a.supersededByCode ? 1 : 0) - (b.supersededByCode ? 1 : 0));
        }

        return {
            key,
            kind,
            members,
            lead: members[0],
            ids: members.reduce((acc: string[], m) => acc.concat(m.ids || []), []),
            message: null,
        };
    });
}

/**
 * Builds the resulting coding set — the record as it would stand if everything pending were
 * accepted, with each line marked for what is happening to it.
 *
 * This is the view the ICD-10 rules are about. "Exactly one primary", "a dagger needs its
 * asterisk", "an injury needs an external cause" are all properties of the SET; none of them can
 * be seen in a list of individual edits, which is how one patient ended up with `add C71.2` and
 * `replace D43.0 → C71.2` both waiting in the queue.
 *
 * Ordered the way a coding set is read: primary, then secondary and comorbidities, then the codes
 * that can never lead — external causes and morphology. Coupled codes are kept adjacent so a pair
 * reads as a pair.
 */
export function buildCodingSet(currentCodes: any[], cards: SuggestionCard[]): CodingSetRow[] {
    const rows: CodingSetRow[] = (currentCodes || []).map((c) => ({
        code: c.code,
        description: c.description,
        role: c.codeRole || null,
        newRole: null,
        state: 'keep' as const,
        decisionKey: null,
        current: c,
        suggestion: null,
    }));

    const byCurrentId = new Map<string, CodingSetRow>();
    rows.forEach((r) => byCurrentId.set(String(r.current.id), r));
    const byCode = new Map<string, CodingSetRow>();
    rows.forEach((r) => { if (!byCode.has(r.code)) byCode.set(r.code, r); });

    // `replacesCodeId` is the authoritative link; matching on code is the fallback for rows
    // captured before the pipeline existed, which no suggestion carries an id for.
    const target = (card: SuggestionCard) =>
        (card.replacesCodeId && byCurrentId.get(String(card.replacesCodeId)))
        || byCode.get(card.code)
        || null;

    for (const card of cards || []) {
        const decisionKey = keyFor(card);
        const displaced = target(card);

        if (card.action === 'remove' || card.action === 'replace') {
            if (displaced) {
                displaced.state = 'removing';
                displaced.decisionKey = decisionKey;
                displaced.suggestion = card;
            }
            if (card.action === 'remove') continue;
        }

        if (card.action === 'reclassify') {
            if (displaced) {
                displaced.state = 'role-change';
                displaced.newRole = card.proposedRole || null;
                displaced.decisionKey = decisionKey;
                displaced.suggestion = card;
            }
            continue;
        }

        rows.push({
            code: card.code,
            description: card.description,
            role: card.proposedRole || null,
            newRole: null,
            // An alternative is not an addition. Drawing both arms of a choice as "being added"
            // would show the set gaining two codes it can never both hold.
            state: card.decisionKind === 'exclusive' ? 'choice' : 'adding',
            decisionKey,
            current: null,
            suggestion: card,
        });
    }

    const ROLE_RANK: { [role: string]: number } = {
        primary: 0, secondary: 1, comorbidity: 2, complication: 3,
        'external-cause': 8, morphology: 9,
    };
    const rank = (row: CodingSetRow) => {
        const role = row.newRole || row.role;
        return role && ROLE_RANK[role] !== undefined ? ROLE_RANK[role] : 4;
    };

    // A coupled group takes the rank of its strongest member, so the pair sorts as one thing and
    // its halves stay next to each other.
    const groupRank = new Map<string, number>();
    for (const row of rows) {
        if (!row.decisionKey) continue;
        const seen = groupRank.get(row.decisionKey);
        if (seen === undefined || rank(row) < seen) groupRank.set(row.decisionKey, rank(row));
    }

    return rows.sort((a, b) => {
        const ra = a.decisionKey ? (groupRank.get(a.decisionKey) as number) : rank(a);
        const rb = b.decisionKey ? (groupRank.get(b.decisionKey) as number) : rank(b);
        if (ra !== rb) return ra - rb;
        if (a.decisionKey !== b.decisionKey) {
            return String(a.decisionKey).localeCompare(String(b.decisionKey));
        }
        return String(a.code).localeCompare(String(b.code));
    });
}

/**
 * How many codes the set would end up marked primary. A claim needs exactly one (§7).
 *
 * Rows on their way off the record do not count, and neither arm of an unmade choice does — both
 * would inflate the number with codes that will not be there.
 */
export function primaryCount(set: CodingSetRow[]): number {
    return survivingRows(set).filter((r) => (r.newRole || r.role) === 'primary').length;
}

/** Rows that will be on the record once the pending decisions are made. */
function survivingRows(set: CodingSetRow[]): CodingSetRow[] {
    return (set || []).filter((r) => r.state !== 'removing' && r.state !== 'choice');
}

/**
 * Rows carrying NO role at all.
 *
 * ⚠ THIS IS WHY THE "no primary diagnosis" BANNER MUST NOT FIRE ON A ZERO COUNT ALONE.
 * `codeRole` has only been captured since the manual path started writing it; on live data
 * 111,610 of 111,611 active codes have none. Every patient with existing codes and no
 * newly-proposed primary was being told "No primary diagnosis. A claim needs exactly one." —
 * on records that may well have one, sitting right there in the list marked "On record".
 *
 * A banner that is wrong almost every time is worse than no banner: it trains a clinician to
 * scroll past the panel, including on the occasions it is right.
 */
export function unknownRoleCount(set: CodingSetRow[]): number {
    return survivingRows(set).filter((r) => !(r.newRole || r.role)).length;
}

/**
 * What to say about the primary diagnosis, or null to stay silent.
 *
 * Silent whenever the answer is unknowable: with any un-roled code surviving, we cannot tell
 * whether a primary exists, so we do not claim one is missing. More than one primary IS
 * knowable — it needs no inference — so that is always reported.
 */
export function primaryWarning(set: CodingSetRow[]): string | null {
    const primaries = primaryCount(set);
    if (primaries > 1) return `${primaries} codes are marked primary. A claim needs exactly one.`;
    if (primaries === 0 && unknownRoleCount(set) === 0 && survivingRows(set).length > 0) {
        return 'No primary diagnosis. A claim needs exactly one.';
    }
    return null;
}

/**
 * The decisions `Accept all` will take, and only those.
 *
 * Deliberately narrow. Removals and replacements take something off the record and are never
 * auto-applied (§7 rule 6) — batching them behind one button is exactly the auto-apply that rule
 * forbids, wearing a different hat. Choices are excluded because a choice has no default: picking
 * for the clinician is not accepting, it is deciding.
 */
export function bulkAcceptable(decisions: Decision[]): Decision[] {
    return (decisions || []).filter((d) =>
        d.kind !== 'exclusive'
        && d.members.every((m) => m.action === 'add' || m.action === 'reclassify'));
}
